import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import duration from 'dayjs/plugin/duration';

import {
  DEFAULT_TIMEZONES,
  FULL_DATE_FORMAT,
  PRETTY_DATE_FORMAT,
  PRETTY_TIME_FORMAT,
  PRETTY_DATE_TIME_FORMAT
} from '../constants/dates';

import { isValidIanaTimezone, FALLBACK_TIMEZONE } from './timezoneValidation';

dayjs.extend(utc);
dayjs.extend(objectSupport);
dayjs.extend(timezone);
dayjs.extend(advanced);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(duration);

// prepend the user time zone if not part of arrray
// guard against browsers/environments where guess() returns a raw UTC offset
// (e.g. '+00:00') instead of an IANA zone name
const guessedTimezone = dayjs.tz.guess();
dayjs.timezoneNames = [
  ...new Set([
    isValidIanaTimezone(guessedTimezone) ? guessedTimezone : FALLBACK_TIMEZONE,
    ...DEFAULT_TIMEZONES
  ])
];

/**
 * Convert timezone code to its shorter abbreviation
 * @param {string} timezoneCode Timezone to abbreviate (ex. America/Vancouver)
 * @returns {string} Abbreviated timezone (ex. PDT)
 */
dayjs.getTimezoneAbbr = (timezoneCode) => {
  return dayjs().tz(timezoneCode).format('z');
};

/**
 * Convert timezone code to its longer but more understandable name
 * @param {string} timezoneCode Timezone code (ex. America/Vancouver)
 * @returns {string} Timezone name (ex. Pacific Daylight Time)
 */
dayjs.getTimezoneName = (timezoneCode) => {
  return dayjs().tz(timezoneCode).format('zzz');
};

/**
 * Convert date to timezone
 * need different behaviour if timezone is already UTC, because there is a dayjs bug for when if we
 * try to change to UTC when already UTC, it reverts to local timezone and messes up the times
 * @param {string | Dayjs | Date} dateTime
 * @param {string} timezoneCode Timezone code (ex. America/Vancouver)
 * @returns {Dayjs} Dayjs datetime in proper timezone
 */
dayjs.convertToTimezone = (dateTime, timezoneCode) => {
  if (dayjs.utc(dateTime).tz(timezoneCode).isUTC()) {
    return dayjs.utc(dateTime);
  }
  return dayjs.utc(dateTime).tz(timezoneCode);
};

/**
 * Get GMT offset for a timezone
 * @param {string} timezoneCode Timezone code (ex. America/Vancouver)
 * @returns {string} Formatted GMT offset (ex. GMT-7)
 */
dayjs.getGMTOffset = (timezoneCode) => {
  const offset = dayjs.utc().tz(timezoneCode).utcOffset() / 60;
  return `GMT${offset >= 0 ? '+' : ''}${offset}`;
};

/**
 * Format a datetime so it can be stored in our database
 * @param {string | Dayjs} dateTime Datetime to format
 * @returns Datetime formatted for our database
 */
dayjs.formatForDB = (dateTime) => {
  return dayjs(dateTime).utc().format(FULL_DATE_FORMAT);
};

/**
 * Format a time
 * @param {Object} dateInfo Information about the event time
 * @param {string} dateInfo.dateTime Datetime to format
 * @param {string} dateInfo.timezoneCode Timezone to format date in (ex. America/Vancouver)
 * @param {string} dateInfo.fromNow Show time from now syntax if recent (ex. 3 hours ago)
 * @returns {string} Formatted date and time
 */
dayjs.formatPrettyDateTime = ({ dateTime, timezoneCode = 'UTC', fromNow, customFormat = null }) => {
  const formattedTime = dayjs
    .utc(dateTime)
    .tz(timezoneCode)
    .format(customFormat || PRETTY_DATE_TIME_FORMAT);
  return fromNow && dayjs().diff(dayjs.utc(dateTime), 'day') < 1
    ? dayjs.utc(dateTime).fromNow()
    : `${formattedTime} ${dayjs.getTimezoneAbbr(timezoneCode)}`;
};

/**
 * Format an event start and end date (not time)
 * @param {Object} dateInfo Information about the event date
 * @param {Dayjs} dateInfo.startDatetime Start time of event
 * @param {Dayjs} dateInfo.endDatetime End time of event
 * @param {boolean} dateInfo.fullDateMonth Include full date/month (ex. Wednesday, June) instead of abbreviation
 * @param {boolean} dateInfo.includeDaySuffix Include st, nd, th, etc (ex. July 2nd)
 * @param {boolean} dateInfo.showYear Include year in result
 * @returns {string} Formatted date(s) (without time)
 */
dayjs.formatEventDate = ({
  startDatetime,
  endDatetime,
  fullDateMonth = false,
  includeDaySuffix = false,
  showYear = false
}) => {
  let dateFormat = PRETTY_DATE_FORMAT;
  if (fullDateMonth) {
    dateFormat = 'dddd, MMMM D';
  }
  if (includeDaySuffix) {
    dateFormat += 'o';
  }
  if (showYear) {
    dateFormat += ' YYYY';
  }
  let formattedDate = startDatetime.format(dateFormat);
  if (!startDatetime.isSame(endDatetime, 'day')) {
    formattedDate += ` – ${endDatetime.format(dateFormat)}`;
  }
  return formattedDate;
};

/**
 * Format an event start and end time (not date)
 * @param {Object} dateInfo Information about the event time
 * @param {Dayjs} dateInfo.startDatetime Start time of event
 * @param {Dayjs} dateInfo.endDatetime End time of event
 * @param {boolean} dateInfo.allDay Is the event all day or not
 * @param {string} dateInfo.displayedTimezone Timezone to display (ex. America/Vancouver)
 * @param {boolean} dateInfo.useSlackTimeFormat Use alternative Slack time formatting
 * @param {boolean} dateInfo.showTimezone Show the timezone of the event
 * @returns {string} Formatted time(s) (without date)
 */
dayjs.formatEventTime = ({
  startDatetime,
  endDatetime,
  allDay,
  displayedTimezone,
  useSlackTimeFormat,
  showTimezone = true
}) => {
  if (allDay) {
    return 'All day';
  }
  let timeFormat = PRETTY_TIME_FORMAT;
  if (useSlackTimeFormat) {
    timeFormat = 'h:mm A';
  }
  let formattedTime = `${startDatetime.format(timeFormat)} – ${endDatetime.format(timeFormat)}`;
  if (showTimezone) {
    formattedTime += ` ${dayjs.getTimezoneAbbr(displayedTimezone)}`;
  }
  return formattedTime;
};

/**
 * Format an event start and end date/time
 * @param {Object} dateInfo Information about the event time
 * @param {string} dateInfo.datetimeScheduled Start time of event
 * @param {string} dateInfo.datetimeEnd End time of event
 * @param {boolean} dateInfo.allDay Is the event all day or not
 * @param {string} dateInfo.eventTimezone The event's timezone (ex. America/Vancouver)
 * @param {string} dateInfo.displayedTimezone Timezone to show the event in (ex. America/Vancouver)
 * @param {boolean} showTime Should the time be shown or not
 * @returns {string} Formatted date(s) and time(s)
 */
dayjs.formatEventDateTime = ({
  datetimeScheduled,
  datetimeEnd,
  allDay,
  eventTimezone = 'UTC',
  displayedTimezone = 'UTC',
  showTime = true
}) => {
  const startDatetime = dayjs.utc(datetimeScheduled).tz(allDay ? eventTimezone : displayedTimezone);
  const endDatetime = dayjs.utc(datetimeEnd).tz(allDay ? eventTimezone : displayedTimezone);

  const formattedDate = dayjs.formatEventDate({ startDatetime, endDatetime });
  const formattedTime = dayjs.formatEventTime({
    startDatetime,
    endDatetime,
    allDay,
    displayedTimezone
  });

  let result = formattedDate;

  if (showTime) {
    result += ` • ${formattedTime}`;
  }

  return result;
};

/**
 * Returns hours/minutes of a datetime
 * @param {Dayjs} instance Dayjs datetime
 * @returns {Object} Hours/minutes (ex. {h: 4, m: 45})
 */
dayjs.getHm = (instance) => {
  return {
    h: instance.hour(),
    m: instance.minute()
  };
};

/**
 * Returns hours/minutes of a datetime/timezone
 * @param {string} dateTime
 * @param {string} timezoneCode Timezone to format date in (ex. America/Vancouver)
 * @returns {Object} Hours/minutes (ex. {h: 4, m: 45})
 */
dayjs.getTimeFromDate = (dateTime, timezoneCode) => {
  return dayjs.getHm(dayjs.utc(dateTime).tz(timezoneCode));
};

/**
 * Update hours/minutes for a date
 * @param {Date|Dayjs|string} dateTime
 * @param {string} timezoneCode Timezone to format date in (ex. America/Vancouver)
 * @param {Object} Hours/minutes to update
 * @returns {string} Updated datetime
 */
dayjs.formatDateFromTime = (dateTime, timezoneCode, { h, m }) => {
  const dateTimeAdjustedForTimezone = dayjs.convertToTimezone(dateTime, timezoneCode);
  return dayjs.formatForDB(dateTimeAdjustedForTimezone.hour(h).minute(m));
};

/**
 * Update date (year/month/day) for a datettime (keep time)
 * @param {string} dateTimeToUpdate Datetime to change
 * @param {Date|Dayjs|string} newDate Year/Month/Day to update to
 * @param {string} timezoneCode Timezone to format date in (ex. America/Vancouver)
 * @returns {Dayjs} Updated datetime
 */
dayjs.updateOnlyDate = (dateTimeToUpdate, newDate, timezoneCode) => {
  const dayjsNewDate = dayjs(newDate);
  return dayjs
    .convertToTimezone(dateTimeToUpdate, timezoneCode)
    .year(dayjsNewDate.year())
    .month(dayjsNewDate.month())
    .date(dayjsNewDate.date());
};

/**
 * Check if two dates are the same day
 * @param {string | Date} first First datetime to compare
 * @param {string | Date} second Second datetime to compare
 * @param {string} timezoneCode Timezone to compare in (ex. America/Vancouver)
 * @returns {boolean}
 */
dayjs.areSameDay = (first, second, timezoneCode) => {
  const firstDateTime = dayjs.utc(first).tz(timezoneCode);
  const secondDateTime = dayjs.utc(second).tz(timezoneCode);
  return (
    firstDateTime.year() === secondDateTime.year() &&
    firstDateTime.month() === secondDateTime.month() &&
    firstDateTime.date() === secondDateTime.date()
  );
};

/**
 * Get the minimum or maximum time if the day is the same
 * @param {string} selectedDateTime
 * @param {string} minMaxDateTime Max or min day
 * @param {string} timezoneCode Datetime with new day/month/year
 * @param {number} offsetMinutes Minutes to offset the minmax datetime by
 * @param {boolean} minTime
 * @returns {Object} Hours/minutes (ex. {h: 4, m: 45})
 */
dayjs.getMinMaxTimeIfSameDay = ({
  selectedDateTime,
  minMaxDateTime,
  timezoneCode,
  offsetMinutes = 0,
  isMinTime
}) => {
  const isSameDay = dayjs.areSameDay(selectedDateTime, minMaxDateTime, timezoneCode);
  if (isSameDay) {
    return dayjs.getHm(dayjs.utc(minMaxDateTime).add(offsetMinutes, 'minute').tz(timezoneCode));
  }

  if (isMinTime && !isSameDay) {
    return undefined;
  }

  return { h: 23, m: 0 };
};

/**
 * Update time to new date (keep time) and/or timezone
 * @param {Object} dateInfo
 * @param {string} dateInfo.dateTime Initial datetime
 * @param {string} dateInfo.timezoneCode
 * @param {string} dateInfo.newDateTime Datetime with new day/month/year
 * @param {string} dateInfo.newTimezoneCode
 * @returns {string} New datetime
 */
dayjs.updateDateTime = ({ dateTime, timezoneCode, newDateTime, newTimezoneCode }) => {
  const oldTimezoneAdjustedDateTime = dayjs.convertToTimezone(dateTime, timezoneCode);
  return dayjs.formatForDB(
    dayjs
      .convertToTimezone(newDateTime || dateTime, newTimezoneCode || timezoneCode)
      .year(oldTimezoneAdjustedDateTime.year())
      .month(oldTimezoneAdjustedDateTime.month())
      .date(oldTimezoneAdjustedDateTime.date())
  );
};

dayjs.convertToStartOfDay = (dateTime, timezoneCode) => {
  // Parse the input timestamp in the provided timezone
  const localTime = dayjs.convertToTimezone(dateTime, timezoneCode);

  // Set the time to the start of the day (12:00 AM)
  const startOfDay = localTime.startOf('day').format();

  return startOfDay;
};

dayjs.subtractOrAddTimeFromDateTime = (
  datetime,
  quantity,
  unit,
  beforeEventBegins,
  timezoneCode
) => {
  // Parse the input timestamp in the provided timezone
  const localTime = dayjs.convertToTimezone(datetime, timezoneCode);

  if (beforeEventBegins) {
    // Subtract the specified amount of time from the parsed timestamp and return dayjs object
    return localTime.subtract(quantity, unit).format();
  } else {
    // Add the specified amount of time to the parsed timestamp and return dayjs object
    return localTime.add(quantity, unit).format();
  }
};

export default dayjs;
